import React, {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react';
import {Button, message} from 'antd';

import {ClockCircleOutlined, DownloadOutlined} from '@ant-design/icons';
import {useParcelDownload} from "../../../../hooks/useParcelDownload";
import { showError } from '../../../../utils/exceptionUtils';
import ParcelsDownloadModal from "../../../central/parcels/components/ParcelsDownloadModal";
import {useMutation, useQuery} from '@apollo/client';
import dayjs from 'dayjs';
import {
    BATCH_MARK_FOR_MANUAL_REVIEW,
    BatchMarkForManualReviewResponse,
    BatchMarkForManualReviewVariables
} from '../../../central/parcels/graphql/mutations/batchMarkForManualReviewMutations';
import AddToPodReviewModal from '../../../central/parcels/components/AddToPodReviewModal';
import {getUserInfo} from "../../../../utils/auth";
import {OrderType} from "../../../../graphql/types/OrderType";
import { allChannelResponse, GET_ALL_CHANNEL } from '../../../../graphql/queries/getChannelAll';
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import BatchInputFilterItem from "../../../delivery/components/BatchInputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import DateTimeRangeFilterItem from "../../../delivery/components/DateTimeRangeFilterItem";
import {
    hasExceptionOptions,
    isComplaintOptions,
    overallStatusOptions,
    podReviewStatusOptions
} from "../../../../config/filterOptions";
import {OrderFiltersType} from "../../../../graphql/types/OrderFiltersType";
import {
    ExceptionDictionaryResponse,
    ExceptionDictionaryType
} from "../../../delivery/parcels/graphql/queries/getAllException";
import {
    GET_ALL_EXCEPTION_FOR_CS,
    GET_NEXT_ACTION_LIST_FOR_CS,
    GET_WAREHOUSES_FOR_CS
} from "../../graphql/queries/customerServiceSharedQueries";
import {filterExceptionByUserRole} from "../../../../config/roleExceptionConfig";
import {
    NextActionListResponse,
    NextActionListType
} from "../../../../graphql/queries/getNextActionList";
import {
    GENERATE_PARCEL_DOWNLOAD_REPORT_FOR_CS,
    GenerateParcelDownloadReportForCsResponse,
    GenerateParcelDownloadReportVariables
} from "../graphql/mutations/generateParcelDownloadReportForCs";
import {formatDateTimeRangeToISO, getLastWeekDateTimeRange} from "../../../../utils/dateTimeUtils";
import { useAutoClearDefaultTime } from '../../../../hooks/useAutoClearDefaultTime';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import {
    GetHubListForOptionResponse,
    GET_HUB_LIST_FOR_OPTIONS
} from "../../../../graphql/queries/warehouseQueries";
import { WarehousesResponse } from '../../../central/exceptions/graphql/queries/getWarehouseForCentral';
import { useTranslation } from 'react-i18next';
import { useStatusInternationalization } from 'pages/delivery/parcels/hooks/useStatusInternationalization';

import { useDspOptions } from '../../../../hooks/useDspOptions';
import {t} from "../../../../i18n";
import { HubSelectFilter } from '../../../../components/filters/HubSelectFilter';
import { WarehouseSelectFilter } from '../../../../components/filters/WarehouseSelectFilter';
import { PickupHubSelectFilter } from '../../../../components/filters/PickupHubSelectFilter';
import { ClientSelectFilter } from '../../../../components/filters/ClientSelectFilter';
import { DspSelectFilter } from '../../../../components/filters/DspSelectFilter';
import { MultiClientSelectFilter } from '../../../../components/filters/MultiClientSelectFilter';
import { MultiPickupHubSelectFilter } from '../../../../components/filters/MultiPickupHubSelectFilter';
import { PrePickupHubSelectFilter } from '../../../../components/filters/PrePickupHubSelectFilter';
import { MultiParcelStatusSelectFilter } from '../../../../components/filters/MultiParcelStatusSelectFilter';
import BagTaskNumberFilterItem from '../../../central/parcels/components/BagTaskNumberFilterItem';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';

interface QCParcelFilterProps {
    filters: OrderFiltersType;
    setFilters: (filters: OrderFiltersType | ((prev: OrderFiltersType) => OrderFiltersType)) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    selectedRows: OrderType[];
    onClearSelection?: () => void; // 添加清空选择的回调函数
}

interface CustomerOption {
    value: string;
    label: string;
}

const QCParcelFilter: React.FC<QCParcelFilterProps> = ({
                                                       filters,
                                                       setFilters,
                                                       handleFilter,
                                                       handleResetFilters,
                                                       selectedRows,
                                                       onClearSelection,
                                                   }) => {
    const {t, i18n} = useTranslation();
    
    // 默认时间范围不写入 URL：用于 UI 默认展示与下载参数构造
    // - undefined：代表“未初始化”，默认展示最近一周
    // - null：代表用户手动清空（点击叉号），必须保持为空，不再回填默认值
    // 一旦用户动过时间（包含点叉号清空），在 reset/刷新前都不再自动清空默认时间
    const hasUserEditedOrderTimeRef = useRef(false);
    useAutoClearDefaultTime({
        filters,
        setFilters: (next) => setFilters(next),
        timeKey: 'orderTime',
        hasUserEditedTimeRef: hasUserEditedOrderTimeRef,
    });

    const getEffectiveOrderTime = useCallback((
        orderTime: OrderFiltersType['orderTime']
    ): [string | null, string | null] | null => {
        if (orderTime === null) return null;
        const isValid = orderTime &&
            Array.isArray(orderTime) &&
            orderTime.length === 2;
        return isValid ? (orderTime as [string | null, string | null]) : getLastWeekDateTimeRange();
    }, []);

    // Date Information（二分）列宽：避免固定 90px 导致新增字段文案压住输入框
    const labelOrderTime = String(t('central.parcels.filters.orderTime'));
    const labelPickupTaskDate = String(t('central.parcels.filters.pickupTaskDate'));
    const labelDeliveredAt = String(t('central.parcels.filters.deliveredAt'));
    const labelHubReceiveDate = String(t('central.parcels.filters.hubReceiveDate'));
    const labelWhsReceiveDate = String(t('central.parcels.filters.whsReceiveDate'));
    const labelLastDeliveryScanTime = String(t('central.parcels.filters.lastDeliveryScanTime'));
    const labelLastTaskDate = String(t('central.parcels.filters.lastTaskDate'));
    const labelLastUpdatedAt = String(t('delivery.parcels.label.lastUpdatedAt'));
    const labelReturnArrivalHubAt = String(t('central.parcels.filters.returnArrivalHubAt'));

    // 三分布局第一列 + 二分左列：需要跨区块对齐，统一用同一个 label 宽度
    const labelTracking = String(t('central.parcels.filters.tracking'));
    const labelOrder = String(t('central.parcels.filters.order'));
    const labelDsp = 'DSP';
    const labelPrePickupHub = String(t('central.parcels.filters.prePickupHub'));
    const labelSortCode1 = String(t('central.parcels.filters.sortCode1'));
    const labelReceiverZipcode = String(t('central.parcels.filters.receiverZipcode'));
    const labelPickupSite = String(t('central.parcels.filters.pickupSite'));
    const labelIsComplaint = String(t('central.parcels.filters.isComplaint'));
    const labelLastExceptionReason = String(t('central.parcels.filters.lastExceptionReason'));
    const labelExceptionStatus = String(t('central.parcels.filters.exceptionStatus'));
    const labelLastExceptionTime = String(t('central.parcels.filters.lastExceptionDate'));
    const labelNextAction = String(t('central.parcels.filters.nextAction'));
    const col1Labels = [
        labelTracking,
        labelOrder,
        labelDsp,
        labelSortCode1,
        labelReceiverZipcode,
        labelPrePickupHub,
        labelIsComplaint,
        labelLastExceptionReason,
        labelExceptionStatus,
    ];
    const dateCol1Labels = [
        labelOrderTime,
        labelDeliveredAt,
        labelWhsReceiveDate,
        labelLastTaskDate,
        labelReturnArrivalHubAt,
        labelLastExceptionTime,
    ];
    const dateCol2Labels = [
        labelPickupTaskDate,
        labelHubReceiveDate,
        labelLastDeliveryScanTime,
        labelLastUpdatedAt,
    ];
    const col1SpanClassName = getSmartLabelClassName([...col1Labels, ...dateCol1Labels]);
    const dateCol1SpanClassName = col1SpanClassName;
    const dateCol2SpanClassName = getSmartLabelClassName(dateCol2Labels);
    const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
    const isSpanish = currentLanguage === 'es';
    const [selectedHubId, setSelectedHubId] = useState<string | undefined>(undefined);
    const [addToPodReviewModalVisible, setAddToPodReviewModalVisible] = useState(false);
    const [addToPodReviewResult, setAddToPodReviewResult] = useState<{
        success: boolean;
        markedCount: number;
        failedCount: number;
        failedTrackingNumbers: string[];
    } | null>(null);
    const [addToPodReviewLoading, setAddToPodReviewLoading] = useState(false);
    const [batchMarkForManualReview] = useMutation<BatchMarkForManualReviewResponse, BatchMarkForManualReviewVariables>(BATCH_MARK_FOR_MANUAL_REVIEW);
    const userRole = getUserInfo()?.account?.role?.name || '';
    const [channelOptions, setChannelOptions] = useState<CustomerOption[]>([]);
    const {data} = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const {data:exceptionData} = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION_FOR_CS);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const [nextActionSelectData, setNextActionSelectData] = useState<NextActionListType[]>([]);
    const {data:nextActionList} = useQuery<NextActionListResponse>(GET_NEXT_ACTION_LIST_FOR_CS);
    const { data: hubWarehouseData } = useQuery<WarehousesResponse>(GET_WAREHOUSES_FOR_CS);
    const exceptionSelectData: ExceptionDictionaryType[] = filterExceptionByUserRole(exceptionList, userRole, undefined);

    // 优化的onChange函数，避免不必要的重新渲染
    // 传递 warehouseId，当清空时同时清空 sortingCodeSegment2（避免状态冲突）
    const handleWarehouseIdChange = useCallback((value: string | undefined) => {
        if (!value) {
            // 清空时，同时清空 sortingCodeSegment2 和 warehouseId
            // 必须明确设置为 undefined，这样 updateHashState 会包含这些字段，encodeHash 会过滤它们，URL hash 中不会包含
            setFilters(prev => ({
                ...prev,
                warehouseId: undefined,
                sortingCodeSegment2: undefined
            }));
        } else {
            // 选择时，只更新 warehouseId，sortingCodeSegment2 由 handleSecondSegmentChange 更新
            setFilters(prev => ({ 
                ...prev, 
                warehouseId: value
            }));
        }
    }, [setFilters]);

    // 新增：处理2nd Sort Code选择时返回对应的Sorting-name
    const handleSortingNameChange = useCallback((sortingName: string | undefined) => {
        // 这里可以根据需要处理Sorting-name，比如存储到filters中或进行其他操作
        // 如果需要将Sorting-name存储到filters中，可以取消下面的注释
        // setFilters(prev => ({ ...prev, sortingName: sortingName }));
    }, [setFilters]);

    // 新增：处理2nd Sort Code选择时返回对应的第二段码
    // 注意：清空时不会调用此回调（由 handleWarehouseIdChange 处理），所以这里只处理选择操作
    const handleSecondSegmentChange = useCallback((secondSegment: string | undefined) => {
        // 只在选择时更新 sortingCodeSegment2，清空时由 handleWarehouseIdChange 统一处理
        if (secondSegment) {
            setFilters(prev => ({ ...prev, sortingCodeSegment2: secondSegment }));
        }
    }, [setFilters]);

    const [generateReportMutation] = useMutation<GenerateParcelDownloadReportForCsResponse, GenerateParcelDownloadReportVariables>(GENERATE_PARCEL_DOWNLOAD_REPORT_FOR_CS);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
    const DOWNLOAD_COOLDOWN = 5000; // 5秒冷却时间

    // 下载完成模态框状态
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState<string>('');


    // 从localStorage获取上次下载时间，页面刷新后仍然有效
    const getLastDownloadTime = (): number => {
        const stored = localStorage.getItem('parcelDownloadLastTime');
        return stored ? parseInt(stored, 10) : 0;
    };

    const setLastDownloadTime = (time: number) => {
        localStorage.setItem('parcelDownloadLastTime', time.toString());
    };

    const [hubOptions, setHubOptions] = useState<CustomerOption[]>([]);
    const {data:hubOptionsData} = useQuery<GetHubListForOptionResponse>(GET_HUB_LIST_FOR_OPTIONS);
    const { getExceptionReasonText, getNextActionTextFromBackend, exceptionOrderStatusOptions } = useStatusInternationalization();

    // 根据选择的站点（sortingCodeSegment1 和 sortingCodeSegment2）找到对应的 warehouse ID
    const selectedWarehouseId = React.useMemo(() => {
        if (!filters.sortingCodeSegment2 || !hubWarehouseData?.warehouses) {
            return undefined;
        }
        // 查找匹配的 warehouse
        const warehouse = hubWarehouseData.warehouses.find(w => {
            if (w.siteType !== 2) return false; // 只查找 warehouse 类型
            const sortingCode = (w as any).sortingCode as string | undefined;
            const segments = sortingCode?.split('-') || [];
            const firstSegment = segments[0];
            const secondSegment = segments.length >= 2 ? segments[1] : undefined;
            // 匹配第一段和第二段
            return firstSegment === filters.sortingCodeSegment1 && secondSegment === filters.sortingCodeSegment2;
        });
        return warehouse?.id ? Number(warehouse.id) : undefined;
    }, [filters.sortingCodeSegment1, filters.sortingCodeSegment2, hubWarehouseData]);

    // DSP选项
    const { dspOptions, loading: dspLoading } = useDspOptions();
    useEffect(() => {
        if (data?.getAllChannels) {
            const channels = Array.isArray(data.getAllChannels) ? data.getAllChannels : [data.getAllChannels];
            const options = channels.map(channel => ({
                value: channel.id,
                label: channel.channelName
            }));
            setChannelOptions(options);
        }
        if (hubOptionsData) {
            const hubs = Array.isArray(hubOptionsData.allOptions) ? hubOptionsData.allOptions : [hubOptionsData.allOptions];
            setHubOptions(hubs);
        }
        if (nextActionList?.getNextActionList){
            setNextActionSelectData(nextActionList.getNextActionList);
        }
    }, [data,nextActionList,hubOptionsData]);

    // 监听重置操作：当第一分拣码被清空时，清空 selectedHubId
    useEffect(() => {
        if (!filters.sortingCodeSegment1 && selectedHubId !== undefined) {
            setSelectedHubId(undefined);
        }
    }, [filters.sortingCodeSegment1, selectedHubId]);

    // 冷却时间倒计时（页面刷新后仍然有效）
    useEffect(() => {
        const checkCooldown = () => {
            const lastTime = getLastDownloadTime();
            if (lastTime > 0) {
                const currentTime = Date.now();
                const remaining = Math.max(0, DOWNLOAD_COOLDOWN - (currentTime - lastTime));
                const remainingSeconds = Math.ceil(remaining / 1000);
                setCooldownRemaining(remainingSeconds);

                if (remaining <= 0) {
                    setCooldownRemaining(0);
                    setDownloadLoading(false);
                    localStorage.removeItem('parcelDownloadLastTime'); // 清理过期数据
                }
            } else {
                setCooldownRemaining(0);
            }
        };

        // 立即检查一次（页面刷新后）
        checkCooldown();

        // 启动定时器，每秒更新一次
        const interval = setInterval(checkCooldown, 1000);

        return () => clearInterval(interval);
    }, [DOWNLOAD_COOLDOWN]);

    // 处理时间变化的函数
    const handleOrderTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        hasUserEditedOrderTimeRef.current = true;
        setFilters({...filters, orderTime: dates});
    };

    const handleResetFiltersWithDefaultTime = () => {
        hasUserEditedOrderTimeRef.current = false;
        handleResetFilters();
        if (onClearSelection) onClearSelection();
    };

    const handleHubReceiveChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, destHubArrivedAt: dates});
    };

    const handleWarehouseReceiveChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, destWarehouseArrivedAt: dates});
    };

    const handleReturnArrivalHubAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, returnArrivalHubAt: dates});
    };

    const handleDeliverTaskChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, deliverAt: dates});
    };

    const handlePickupAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, receivedAtHub: dates});
    };

    const handleExceptionTimeChange = (
        dates: [string | null, string | null] | null,
    ) => {
        setFilters({...filters, markExceptionAt: dates});
    };

    const handleDeliveredAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, deliveredAt: dates});
    };

    const handleLastDeliveryScanTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, lastDeliveryScanTime: dates});
    };

    const handleUpdatedAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, updatedAt: dates});
    };

    const handleBagTaskNumberChange = (value: string | undefined) => {
        setFilters({
            ...filters,
            bagTaskNumbers: value
        });
    };

    const copyTrackingNumbers = useCallback(async () => {
        if (!selectedRows || selectedRows.length === 0) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const trackingNumbers = selectedRows
            .map(row => row.trackingNumber?.trim())
            .filter((tracking): tracking is string => Boolean(tracking && tracking.length > 0));
        if (trackingNumbers.length === 0) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const text = trackingNumbers.join('\n');
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            message.success(t('common.messages.copied'));
        } catch (error) {
            console.error('Copy tracking numbers failed:', error);
            message.error(t('common.messages.copyFailed'));
        }
    }, [selectedRows, t]);


    // 构建查询参数 - 与useCentralParcelQuery中的buildQueryVariables保持一致
    const buildQueryFilters = (filters: OrderFiltersType) => {
        const timeRanges = {
            orderTime: formatDateTimeRangeToISO(getEffectiveOrderTime(filters.orderTime)),
            receivedAtHub: formatDateTimeRangeToISO(filters.receivedAtHub),
            destHubArrivedAt: formatDateTimeRangeToISO(filters.destHubArrivedAt),
            destWarehouseArrivedAt: formatDateTimeRangeToISO(filters.destWarehouseArrivedAt),
            deliverAt: formatDateTimeRangeToISO(filters.deliverAt),
            markExceptionAt: formatDateTimeRangeToISO(filters.markExceptionAt),
            deliveredAt: formatDateTimeRangeToISO(filters.deliveredAt),
            lastDeliveryScanTime: formatDateTimeRangeToISO(filters.lastDeliveryScanTime),
            updatedAt: formatDateTimeRangeToISO(filters.updatedAt),
            returnArrivalHubAt: formatDateTimeRangeToISO(filters.returnArrivalHubAt)
        };

        // 处理 receiverZipcode：如果有原始输入，转换为数组（与查询列表保持一致）
        let receiverZipcode: string[] | undefined = filters.receiverZipcode;
        if (filters.receiverZipcodeInput) {
            // 支持换行符、半角逗号和空格分隔（多个空格会被视为一个分隔符）
            const zipcodes = filters.receiverZipcodeInput
                .split(/[\r\n,\s]+/)  // 按换行符、逗号或空格分割（\s+ 匹配一个或多个空白字符）
                .map(z => z.trim())
                .filter(z => z.length > 0);
            receiverZipcode = zipcodes.length > 0 ? zipcodes : undefined;
        }

        // 与查询列表保持一致的过滤项构造
        const queryFilters: any = {
            ...filters,
            ...timeRanges,
            // 转换数组类型为多值查询
            channelIds: filters.channelId?.map(id => parseInt(id)) || undefined,
            pickupHubIds: filters.pickupHubId?.map(id => parseInt(id)) || undefined,
            overallStatuses: filters.overallStatus || undefined,
            nextActions: (filters.nextActions && filters.nextActions.length > 0) ? filters.nextActions : undefined,
            deliveredDspIds: (filters.deliveredDspIds && filters.deliveredDspIds.length > 0) ? filters.deliveredDspIds : undefined,
            receiverZipcode: receiverZipcode, // 使用转换后的数组
            pickupTaskDate: undefined, // 不发送到后端
            // 转换 hubId 和 warehouseId 为数字类型（与 useCentralParcelQuery 保持一致）
            hubId: filters.hubId ? parseInt(filters.hubId) : undefined,
            warehouseId: filters.warehouseId ? parseInt(filters.warehouseId) : undefined,
            // 明确保留分拣码段字段，确保筛选条件正确传递
            sortingCodeSegment1: filters.sortingCodeSegment1 || undefined,
            sortingCodeSegment2: filters.sortingCodeSegment2 || undefined,
            sortingCodeSegment3: filters.sortingCodeSegment3 || undefined
        };

        // 清空临时输入字段，不发送到后端
        queryFilters.receiverZipcodeInput = undefined;

        // 清空单值字段，避免与多值字段冲突
        queryFilters.channelId = undefined;
        queryFilters.pickupHubId = undefined;
        queryFilters.overallStatus = undefined;
        queryFilters.nextAction = undefined;
        queryFilters.deliveredDspId = undefined;

        // 移除无效的时间范围字段，避免GraphQL schema错误
        Object.keys(queryFilters).forEach(key => {
            const value = queryFilters[key];
            if (value === undefined) {
                delete queryFilters[key];
                return;
            }
            if (typeof value === 'object' && value !== null) {
                const hasStart = Object.prototype.hasOwnProperty.call(value, 'start');
                const hasEnd = Object.prototype.hasOwnProperty.call(value, 'end');
                if ((hasStart || hasEnd) && value.start === undefined && value.end === undefined) {
                    delete queryFilters[key];
                }
            }
        });

        return queryFilters;
    };

    // Download handler function
    const handleDownloadParcels = async () => {
        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();

        // Check cooldown time
        if (currentTime - lastTime < DOWNLOAD_COOLDOWN) {
            const remainingTime = Math.ceil((DOWNLOAD_COOLDOWN - (currentTime - lastTime)) / 1000);
            message.warning(`Please wait ${remainingTime} seconds before trying again to avoid duplicate requests`);
            return;
        }

        // Check if already loading
        if (downloadLoading) {
            message.warning('Report is being generated, please do not click repeatedly');
            return;
        }

        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime); // Store to localStorage

            // 获取用户时区
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            // 使用与filter按钮完全相同的参数构建逻辑
            const queryFilters = buildQueryFilters(filters);
            // 调试日志：打印构建的查询参数
            console.log('📥 [Download] 构建的查询参数:', JSON.stringify(queryFilters, null, 2));

            // 如果有选中的行，只下载选中的数据
            if (selectedRows && selectedRows.length > 0) {
                const trackingNumbers = selectedRows
                    .map(row => row.trackingNumber)
                    .filter((tn): tn is string => !!tn);

                if (trackingNumbers.length > 0) {
                    // 使用选中的 trackingNumbers 覆盖过滤条件
                    queryFilters.trackingNumbers = trackingNumbers.join(';');
                }
            }

            const response = await generateReportMutation({
                variables: {
                    filters: queryFilters,
                    timezone: userTimezone
                }
            });

            const fileName = response.data?.generateParcelDownloadReportForCs;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadCompleteModalVisible(true);
            } else {
                message.error('Failed to start report generation task');
            }
        } catch (error: any) {
            console.error('Report generation failed:', error);
            showError(error);
            setDownloadLoading(false);
        }
    };


    // 添加到POD人审队列处理函数
    const handleAddToPodReview = async () => {
        // 防重复点击 - 放在最前面
        if (addToPodReviewLoading) {
            return;
        }

        // 立即设置loading状态，防止重复点击
        setAddToPodReviewLoading(true);

        if (selectedRows.length < 1) {
            message.warning(t('podReview.addToPodReview.selectAtLeastOneParcel'));
            // 短暂延迟后重置loading状态
            setTimeout(() => setAddToPodReviewLoading(false), 1000);
            return;
        }

        // 检查运单状态，只有妥投状态才能添加到人审
        const nonDeliveredOrders = selectedRows.filter(row => row.overallStatus !== 'DELIVERED');
        if (nonDeliveredOrders.length > 0) {
            message.warning(t('podReview.messages.onlyDeliveredOrdersCanAddToPodReview'));
            // 短暂延迟后重置loading状态
            setTimeout(() => setAddToPodReviewLoading(false), 1000);
            return;
        }
        try {
            // 获取选中行的订单ID
            const orderIds = selectedRows.map(row => parseInt(row.id)).filter(id => !isNaN(id));

            if (orderIds.length === 0) {
                message.warning(t('podReview.addToPodReview.noValidOrderIds'));
                return;
            }

            // 调用后端API标记为待人工审核
            const {data} = await batchMarkForManualReview({
                variables: {
                    orderIds: orderIds
                },
                refetchQueries: ['CentralQuery']
            });

            if (data?.batchMarkForManualReview) {
                const result = data.batchMarkForManualReview;
                setAddToPodReviewResult(result);
                setAddToPodReviewModalVisible(true);
            } else {
                message.error(t('podReview.addToPodReview.failedToAdd'));
            }
        } catch (error: any) {
            console.error('Add to POD review error:', error);
            message.error(t('podReview.addToPodReview.failedToAdd'));
        } finally {
            setAddToPodReviewLoading(false);
        }
    };

    const labelClassName = useFilterLabelClassName();
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();

    return (
        <div className="mb-2 py-2">
            {/* Base Information */}
            <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.baseInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="col-span-3">
                    <BatchInputFilterItem
                        label={t('central.parcels.filters.tracking')}
                        value={filters.trackingNumbers}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({
                            ...filters,
                            trackingNumbers: e.target.value || undefined
                        })}
                                spanClassName={col1SpanClassName}
                        placeholder={t('central.parcels.placeholders.trackingNumbers') || ''}
                        rows={2}
                    />
                        </div>
                        <div className="min-w-0">
                <BatchInputFilterItem
                    label={t('central.parcels.filters.order')}
                    value={filters.channelOrderNumber}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({...filters, channelOrderNumber: e.target.value || undefined})}
                    spanClassName={col1SpanClassName}
                    rows={1}
                />
                        </div>
                        <div className="min-w-0">
                <BatchInputFilterItem
                    label={t('central.parcels.filters.externalTrackingNumber')}
                    value={filters.externalTrackingNumber}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({...filters, externalTrackingNumber: e.target.value || undefined})}
                    spanClassName={col1SpanClassName}
                    rows={1}
                />
                        </div>
                        <div className="min-w-0">
                <InputFilterItem
                    label={t('central.parcels.filters.sortingChannelOrderNumber')}
                    value={filters.sortingChannelOrderNumber}
                    onChange={(e) => setFilters({...filters, sortingChannelOrderNumber: e.target.value || undefined})}
                    spanClassName="!w-[90px] !min-w-0 !flex-shrink-0 !font-bold"
                />
                        </div>
                        <div className="min-w-0">
                <DspSelectFilter
                    label="DSP"
                    value={filters.deliveredDspIds?.map(String)}
                    onChange={(value: string[] | undefined) => setFilters({...filters, deliveredDspIds: value?.map(v => parseInt(v)), deliveredDspId: undefined})}
                                spanClassName={col1SpanClassName}
                    placeholder={t('components.input.all') as string}
                    warehouseId={selectedWarehouseId}
                />
                        </div>
                        <div className="min-w-0">
                <MultiClientSelectFilter
                    label={t('central.parcels.filters.client')}
                    value={filters.channelId}
                    onChange={(value: string[] | undefined) => setFilters({...filters, channelId: value})}
                                spanClassName="!w-[90px] !min-w-0 !flex-shrink-0 !font-bold"
                    placeholder={t('components.input.all') as string}
                />
                        </div>
                        <div className="min-w-0">
                <MultiParcelStatusSelectFilter
                    label={t('central.parcels.filters.parcelStatus')}
                    value={filters.overallStatus}
                    onChange={(value: string[] | undefined) => setFilters({...filters, overallStatus: value})}
                                spanClassName="!w-[90px] !min-w-0 !flex-shrink-0 !font-bold"
                    placeholder={t('components.input.all') as string}
                />
                        </div>
                </div>
                </div>
            </div>

            {/* Route Information */}
            <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.routeInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                <HubSelectFilter
                    label={t('central.parcels.filters.sortCode1')}
                    value={filters.sortingCodeSegment1}
                    onChange={(value, hubId) => {
                        if (!value) {
                            const newFilters = {
                                ...filters,
                                sortingCodeSegment1: undefined,
                                hubId: undefined,
                                warehouseId: undefined
                            };
                            setFilters(newFilters);
                            setSelectedHubId(undefined);
                            handleFilter();
                        } else {
                            const newFilters = {
                                ...filters,
                                sortingCodeSegment1: value,
                                hubId: hubId,
                                            warehouseId: undefined
                            };
                            setFilters(newFilters);
                                        setSelectedHubId(hubId);
                            handleFilter();
                        }
                    }}
                                spanClassName={col1SpanClassName}
                    placeholder={t('central.parcels.placeholders.selectHub') || 'Select HUB'}
                />
                        </div>
                        <div className="min-w-0">
                <WarehouseSelectFilter
                    label={t('central.parcels.filters.sortCode2')}
                    value={filters.warehouseId}
                    onChange={handleWarehouseIdChange}
                    onSelectSortingName={handleSortingNameChange}
                    onSelectSecondSegment={handleSecondSegmentChange}
                                spanClassName="!w-[90px] !min-w-0 !flex-shrink-0 !font-bold"
                                hubId={selectedHubId}
                    placeholder={t('central.parcels.placeholders.selectWarehouse') || 'Select Warehouse'}
                />
                        </div>
                        <div className="min-w-0">
                <InputFilterItem
                    label={t('central.parcels.filters.sortCode3')}
                    value={filters.sortingCodeSegment3}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({...filters, sortingCodeSegment3: e.target.value || undefined})}
                                spanClassName="!w-[90px] !min-w-0 !flex-shrink-0 !font-bold"
                />
                        </div>
                        <div className="min-w-0">
                <BatchInputFilterItem
                    label={t('central.parcels.filters.receiverZipcode')}
                    value={filters.receiverZipcodeInput || filters.receiverZipcode?.join('\n') || ''}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                        const value = e.target.value;
                        setFilters({
                            ...filters,
                            receiverZipcodeInput: value || undefined
                        });
                    }}
                                placeholder={t('central.parcels.placeholders.receiverZipcode') || 'Enter multiple zipcodes'}
                                spanClassName={col1SpanClassName}
                    rows={1}
                />
                        </div>
                        <div className="min-w-0">
                <BagTaskNumberFilterItem
                    label={t('central.parcels.filters.bagTaskNumber')}
                    value={filters.bagTaskNumbers}
                    onChange={handleBagTaskNumberChange}
                                spanClassName="!w-[90px] !min-w-0 !flex-shrink-0 !font-bold"
                    placeholder={t('central.parcels.placeholders.bagTaskNumber') || 'Enter bagging task'}
                />
                        </div>
                        <div className="min-w-0">
                <MultiPickupHubSelectFilter
                    label={t('delivery.parcels.label.pickupHub')}
                    value={filters.pickupHubId}
                    onChange={(value: string[] | undefined) => setFilters({...filters, pickupHubId: value})}
                                spanClassName="!w-[90px] !min-w-0 !flex-shrink-0 !font-bold"
                    placeholder={t('components.input.all') as string}
                />
                        </div>
                        <div className="min-w-0">
                <PrePickupHubSelectFilter
                    label={labelPrePickupHub}
                    value={filters.prePickupHubName}
                    onChange={(name: string | undefined) => {
                        setFilters({ ...filters, prePickupHubName: name });
                    }}
                    spanClassName={col1SpanClassName}
                    placeholder={t('central.parcels.placeholders.prePickupHub') || ''}
                />
                        </div>
                        <div className="min-w-0">
                <InputFilterItem
                    label={labelPickupSite}
                    value={filters.cooperationWarehouseId?.toString()}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value.trim();
                        const numValue = value ? parseInt(value, 10) : undefined;
                        setFilters({
                            ...filters,
                            cooperationWarehouseId: (numValue && !isNaN(numValue)) ? numValue : undefined
                        });
                    }}
                    spanClassName="!w-[90px] !min-w-0 !flex-shrink-0 !font-bold"
                    inputClassName="h-8"
                    inputStyle={{ paddingTop: 0, paddingBottom: 0, lineHeight: '32px' }}
                />
                        </div>
                        <div className="min-w-0"></div>
                </div>
                </div>
            </div>

            {/* Date Information */}
            <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.dateInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 日期信息精确到分：采用二分布局（两列）以避免挤压 */}
                    <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelOrderTime}
                                value={getEffectiveOrderTime(filters.orderTime)}
                                onChange={handleOrderTimeChange}
                                spanClassName={dateCol1SpanClassName}
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelPickupTaskDate}
                                value={filters.receivedAtHub}
                                onChange={handlePickupAtChange}
                                spanClassName={dateCol2SpanClassName}
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelDeliveredAt}
                                value={filters.deliveredAt}
                                onChange={handleDeliveredAtChange}
                                spanClassName={dateCol1SpanClassName}
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelHubReceiveDate}
                                value={filters.destHubArrivedAt}
                                onChange={handleHubReceiveChange}
                                spanClassName={dateCol2SpanClassName}
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelWhsReceiveDate}
                                value={filters.destWarehouseArrivedAt}
                                onChange={handleWarehouseReceiveChange}
                                spanClassName={dateCol1SpanClassName}
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelLastDeliveryScanTime}
                                value={filters.lastDeliveryScanTime}
                                onChange={handleLastDeliveryScanTimeChange}
                                spanClassName={dateCol2SpanClassName}
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelLastTaskDate}
                                value={filters.deliverAt}
                                onChange={handleDeliverTaskChange}
                                spanClassName={dateCol1SpanClassName}
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelLastUpdatedAt}
                                value={filters.updatedAt}
                                onChange={handleUpdatedAtChange}
                                spanClassName={dateCol2SpanClassName}
                            />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelReturnArrivalHubAt}
                                value={filters.returnArrivalHubAt}
                                onChange={handleReturnArrivalHubAtChange}
                                spanClassName={dateCol1SpanClassName}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Exception Information */}
            <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.exceptionInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={t('central.parcels.filters.isComplaint')}
                    options={isComplaintOptions}
                    value={filters.isComplaint?.toString() ?? undefined}
                    onChange={(value: string) => setFilters({...filters, isComplaint: value ? (value === 'true') : undefined})}
                                spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={t('delivery.parcels.label.podReviewStatus')}
                    options={podReviewStatusOptions}
                    value={filters.podReviewStatus ?? undefined}
                    onChange={(value: string) => setFilters({...filters, podReviewStatus: value || undefined})}
                                spanClassName="!w-[90px] !min-w-0 !flex-shrink-0 !font-bold"
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={t('central.parcels.filters.hasException')}
                    options={hasExceptionOptions}
                    value={filters.isHasBeenSetException?.toString() ?? undefined}
                    onChange={(value: string) => setFilters({...filters, isHasBeenSetException: value ? (value === 'true') : undefined})}
                                spanClassName="!w-[90px] !min-w-0 !flex-shrink-0 !font-bold"
                />
                        </div>
                        {/* 第二行：上次异常原因 / 异常状态 / 下一步动作 */}
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={t('central.parcels.filters.lastExceptionReason')}
                    options={exceptionList.map(ex => ({
                        value: ex.exceptionReasonCode,
                        label: getExceptionReasonText(ex, ex.exceptionReasonCode)
                    })) || []}
                    value={filters.exceptionReasonCode ?? undefined}
                    onChange={(value: string) => setFilters({...filters, exceptionReasonCode: value || undefined})}
                                spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelExceptionStatus}
                    options={exceptionOrderStatusOptions}
                    mode="multiple"
                    maxTagCount="responsive"
                    value={filters?.exceptionStatuses?.map(String) ?? []}
                    onChange={(values: string[]) => setFilters({...filters, exceptionStatuses: values && values.length > 0 && values.length < exceptionOrderStatusOptions.length ? values.map(Number) : undefined})}
                    spanClassName="!w-[90px] !min-w-0 !flex-shrink-0 !font-bold"
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelNextAction}
                    options={nextActionSelectData.filter(item => item.value !== 0).map(item => ({
                        value: item.name,
                        label: getNextActionTextFromBackend(item.name)
                    }))}
                    mode="multiple"
                    value={filters.nextActions}
                    onChange={(value: string[] | undefined) => setFilters({...filters, nextActions: value, nextAction: undefined})}
                                spanClassName="!w-[90px] !min-w-0 !flex-shrink-0 !font-bold"
                />
                        </div>
                </div>

                {/* 第三行（二分）：上次异常时间（到分钟） */}
                <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start mt-4">
                    <div className="min-w-0">
                        <DateTimeRangeFilterItem
                            label={labelLastExceptionTime}
                            value={filters.markExceptionAt}
                            onChange={handleExceptionTimeChange}
                            spanClassName={col1SpanClassName}
                            format="YYYY/MM/DD HH:mm"
                            showTime={{
                                format: 'HH:mm',
                                defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                            }}
                        />
                    </div>
                    <div className="min-w-0"></div>
                </div>
                </div>
            </div>

            <div className="flex justify-between items-center mb-3">
                <div className="flex gap-2">
                            <Button
                                type="primary"
                                onClick={handleAddToPodReview}
                                loading={addToPodReviewLoading}
                        className="w-auto min-w-24"
                            >
                        <span className="font-bold">{t('podReview.addToPodReview.title')}</span>
                            </Button>
                            <Button
                                onClick={copyTrackingNumbers}
                        className="w-auto min-w-24"
                            >
                        <span className="font-bold">{t('common.buttons.copyTracking')}</span>
                            </Button>
                        </div>

                <div className="flex gap-2">
                            <Button
                                type="primary"
                                onClick={handleDownloadParcels}
                                loading={cooldownRemaining > 0}
                                disabled={downloadLoading || cooldownRemaining > 0}
                        className="w-auto min-w-24"
                            >
                        <span className="font-bold">
                                {cooldownRemaining > 0
                                    ? `Download ${cooldownRemaining}s`
                                : t('components.button.download')
                                }
                        </span>
                            </Button>
                            <Button
                                type="primary"
                        onClick={handleFilter}
                        className="w-auto min-w-24"
                            >
                        <span className="font-bold">{t('components.button.filter')}</span>
                            </Button>
                    <Button 
                        onClick={handleResetFiltersWithDefaultTime}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.reset')}</span>
                            </Button>
                        </div>
            </div>

            <ParcelsDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
            />

            <AddToPodReviewModal
                visible={addToPodReviewModalVisible}
                onClose={() => setAddToPodReviewModalVisible(false)}
                result={addToPodReviewResult}
            />
        </div>
    );
};

export default QCParcelFilter;